import React from "react";
import { useUserUiPreferences } from "./useUserUiPreferences.js";
import {
  applyActiveNavStateToSections,
  getActiveNavKeyFromSections,
  getInitialOpenNavItemKey,
  getPathnameNavKey,
} from "../utils/appShellNavigation.js";
import {
  dispatchResultsShellNavigation,
  getDetailViewFromLocation,
  getResultsDefaultNavKey,
  resolveDetailViewFromHrefOrName,
  resolveDispatchShellNavigationView,
  shouldHandleInPlaceShellNavigation,
} from "../utils/resultsDetailViewNavigation.js";

export default function useShellNavigation({ isResultsPage, location, navigate, navSections, compact = false }) {
  const pathname = location.pathname;
  // Aucune entrée du menu ne mène à un onglet du Dispatch : « Dispatch » reste actif sur tous ses onglets.
  const activeNavKey = React.useMemo(() => {
    if (isResultsPage) {
      return getResultsDefaultNavKey(pathname) || getActiveNavKeyFromSections(navSections);
    }

    return getPathnameNavKey(pathname);
  }, [isResultsPage, navSections, pathname]);

  const resolvedNavSections = React.useMemo(() => (
    applyActiveNavStateToSections(navSections, activeNavKey)
  ), [activeNavKey, navSections]);

  const { sidebarCollapsed: desktopSidebarCollapsed, setSidebarCollapsed: saveSidebarCollapsed } = useUserUiPreferences();
  const sidebarCollapsed = !compact && desktopSidebarCollapsed;
  const setSidebarCollapsed = React.useCallback((value) => {
    // The global provider displays save errors and restores the confirmed value.
    void saveSidebarCollapsed(value).catch(() => {});
  }, [saveSidebarCollapsed]);
  const [openNavItemKey, setOpenNavItemKey] = React.useState(() => getInitialOpenNavItemKey(resolvedNavSections));

  const toggleNavItem = React.useCallback((itemKey) => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      setOpenNavItemKey(itemKey);
      return;
    }

    setOpenNavItemKey((previousOpenNavItemKey) => (
      previousOpenNavItemKey === itemKey ? null : itemKey
    ));
  }, [setSidebarCollapsed, sidebarCollapsed]);

  const handleNavItemNavigate = React.useCallback((event, item) => {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.altKey
      || event.ctrlKey
      || event.shiftKey
    ) {
      return;
    }

    if (item?.is_disabled === true) {
      event.preventDefault();
      return;
    }

    if (!isResultsPage) {
      if (item?.detail_view_link && typeof item?.href === "string" && item.href.trim()) {
        event.preventDefault();
        navigate(item.href);
      }
      return;
    }

    const requestedView = resolveDetailViewFromHrefOrName({
      detailViewLink: item?.detail_view_link,
      href: item?.href,
    });
    const handlesInPlaceNavigation = shouldHandleInPlaceShellNavigation(item?.href, pathname);

    if (!handlesInPlaceNavigation) {
      return;
    }

    event.preventDefault();

    if (!requestedView) {
      const currentDetailView = typeof window !== "undefined"
        ? getDetailViewFromLocation(window.location)
        : getDetailViewFromLocation(location);

      dispatchResultsShellNavigation({
        historyMode: "push",
        scroll: false,
        source: "shell-nav",
        syncLocation: true,
        viewName: resolveDispatchShellNavigationView(currentDetailView),
      });
      return;
    }

    dispatchResultsShellNavigation({
      errorsOpenAll: item?.errors_open_all === true,
      forceOpen: item?.detail_force_open === true,
      historyMode: "push",
      scroll: true,
      source: "shell-nav",
      syncLocation: true,
      viewName: requestedView,
    });
  }, [isResultsPage, navigate, pathname]);

  React.useEffect(() => {
    const nextOpenNavItemKey = getInitialOpenNavItemKey(resolvedNavSections);
    setOpenNavItemKey(nextOpenNavItemKey);
  }, [resolvedNavSections]);

  return {
    activeNavKey,
    handleNavItemNavigate,
    openNavItemKey,
    resolvedNavSections,
    setSidebarCollapsed,
    sidebarCollapsed,
    toggleNavItem,
  };
}
